import { formatMemoryClass } from '../../lib/document-kind'
import type { ReactNode } from 'react'
import type { MemoryClassFacet, SourceFacet } from '../../lib/knowledge-library-state'
import type {
  LibraryAccess,
  LibraryAuthority,
  LibraryFixedView,
  LibraryMemoryClass,
  LibraryOwnership,
  LibraryQueryState,
  LibrarySort,
  LibrarySortOrder,
  LibrarySyncHealth,
} from '../../lib/knowledge-library-query-state'

const fixedViews: Array<{ value: LibraryFixedView; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'needs-review', label: 'Needs review' },
  { value: 'decisions', label: 'Decisions' },
  { value: 'runbooks', label: 'Runbooks' },
  { value: 'handoffs', label: 'Handoffs' },
]

const ownershipOptions: Array<{ value: LibraryOwnership; label: string }> = [
  { value: 'all', label: 'All ownership' },
  { value: 'manual', label: 'Manual document' },
  { value: 'source-backed', label: 'Source-backed' },
]

const accessOptions: Array<{ value: LibraryAccess; label: string }> = [
  { value: 'all', label: 'All access' },
  { value: 'private', label: 'Private access' },
  { value: 'specific', label: 'Specific access' },
  { value: 'public', label: 'Public access' },
]

const syncOptions: Array<{ value: LibrarySyncHealth; label: string }> = [
  { value: 'all', label: 'All sync health' },
  { value: 'verified', label: 'Verified' },
  { value: 'stale', label: 'Stale' },
  { value: 'conflict', label: 'Sync conflict' },
]

const authorityOptions: Array<{ value: LibraryAuthority; label: string }> = [
  { value: 'all', label: 'All authority' },
  { value: 'active', label: 'Active' },
  { value: 'reference', label: 'Reference' },
  { value: 'needs-review', label: 'Needs review' },
  { value: 'superseded', label: 'Superseded' },
  { value: 'expired', label: 'Expired' },
]

export interface KnowledgeLibraryFacetsProps {
  state: LibraryQueryState
  sourceFacets: SourceFacet[]
  memoryClassFacets: MemoryClassFacet[]
  authorityAvailable: boolean
  updateState: (next: Partial<LibraryQueryState>) => void
}

function FacetSelect({
  label,
  value,
  onChange,
  children,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  children: ReactNode
}) {
  return (
    <label className="aw-knowledge-facet-select">
      <span>{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        {children}
      </select>
    </label>
  )
}

export function KnowledgeLibraryFacets({
  state,
  sourceFacets,
  memoryClassFacets,
  authorityAvailable,
  updateState,
}: KnowledgeLibraryFacetsProps) {
  const selectedSourceLoaded = sourceFacets.some((source) => source.key === state.source)
  const selectedClassLoaded = memoryClassFacets.some((item) => item.key === state.memoryClass)

  return (
    <aside className="aw-knowledge-facets" aria-label="Knowledge filters">
      <div className="aw-knowledge-facet-group">
        <span className="aw-knowledge-facet-label">Views</span>
        <div className="aw-knowledge-fixed-views">
          {fixedViews.map((view) => {
            const authorityBlocked = view.value === 'needs-review' && !authorityAvailable
            return (
              <button
                key={view.value}
                type="button"
                aria-pressed={state.view === view.value}
                aria-disabled={authorityBlocked || undefined}
                aria-describedby={authorityBlocked ? 'authority-view-availability' : undefined}
                onClick={() => {
                  if (!authorityBlocked) updateState({ view: view.value })
                }}
              >
                {view.label}
              </button>
            )
          })}
        </div>
        {!authorityAvailable && (
          <p id="authority-view-availability" className="aw-knowledge-facet-note">
            Authority views will appear when human-governed metadata is available.
          </p>
        )}
      </div>

      <div className="aw-knowledge-facet-grid">
        <FacetSelect
          label="Ownership"
          value={state.ownership}
          onChange={(value) => updateState({ ownership: value as LibraryOwnership })}
        >
          {ownershipOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </FacetSelect>
        <FacetSelect
          label="Access"
          value={state.access}
          onChange={(value) => updateState({ access: value as LibraryAccess })}
        >
          {accessOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </FacetSelect>
        <FacetSelect
          label="Source"
          value={state.source}
          onChange={(value) => updateState({ source: value })}
        >
          <option value="all">All loaded sources</option>
          {!selectedSourceLoaded && state.source !== 'all' && (
            <option value={state.source}>Selected source (not loaded)</option>
          )}
          {sourceFacets.map((source) => (
            <option key={source.key} value={source.key}>
              {source.label} ({source.count})
            </option>
          ))}
        </FacetSelect>
        <FacetSelect
          label="Sync health"
          value={state.sync}
          onChange={(value) => updateState({ sync: value as LibrarySyncHealth })}
        >
          {syncOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </FacetSelect>
        {authorityAvailable && (
          <FacetSelect
            label="Authority"
            value={state.authority}
            onChange={(value) => updateState({ authority: value as LibraryAuthority })}
          >
            {authorityOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </FacetSelect>
        )}
        <FacetSelect
          label="Memory class"
          value={state.memoryClass}
          onChange={(value) => updateState({ memoryClass: value as LibraryMemoryClass })}
        >
          <option value="all">All memory classes</option>
          {!selectedClassLoaded && state.memoryClass !== 'all' && (
            <option value={state.memoryClass}>Selected class (not loaded)</option>
          )}
          {memoryClassFacets.map((item) => (
            <option key={item.key} value={item.key}>
              {formatMemoryClass(item.key)} ({item.count})
            </option>
          ))}
        </FacetSelect>
        <FacetSelect
          label="Sort by"
          value={state.sort}
          onChange={(value) => updateState({ sort: value as LibrarySort })}
        >
          <option value="updated">Updated</option>
          <option value="created">Created</option>
          <option value="title">Title</option>
        </FacetSelect>
        <FacetSelect
          label="Order"
          value={state.order}
          onChange={(value) => updateState({ order: value as LibrarySortOrder })}
        >
          <option value="desc">Descending</option>
          <option value="asc">Ascending</option>
        </FacetSelect>
      </div>
    </aside>
  )
}
